<script lang="ts">
	import mapImageXs from "@assets/images/contact-map.png?webp&w=500&imagetools";
	import mapImageSm from "@assets/images/contact-map.png?webp&w=600&imagetools";
	import mapImageMd from "@assets/images/contact-map.png?webp&w=700&imagetools";
	import mapImageLg from "@assets/images/contact-map.png?webp&w=800&imagetools";
	import ContactForm from "@components/ContactForm.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Media from "@components/common/Media.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { CafeOrCoffeeShop } from "schema-dts";
	import facebookIcon from "@core/assets/icons/facebook.svg?raw";
	import instagramIcon from "@core/assets/icons/instagram.svg?raw";
	import youtubeIcon from "@core/assets/icons/youtube.svg?raw";
	import { fathersCompany } from "@core/schema/fathersCompany.js";
	import { appName } from "@core/schema/names/appName.js";
	import { getAboutUsPath } from "@core/schema/paths/getAboutUsPath.js";
	import { getCoffeeStoreAnchor } from "@core/schema/paths/getCoffeeStoreAnchor.js";
	import { formatPhoneNumber } from "@core/utils/formatPhoneNumber.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./Contact.translations.json" with { type: "json" };
	import type { ContactProps } from "./ContactProps.js";

	const _: ContactProps = $props();
	const translate = getTranslate(translations);
	const { WEB_URL } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	const organizationRef = { "@type": "Organization" as const, "@id": `${WEB_URL}/#organization` };

	const roasterySchema = {
		"@type": "LocalBusiness",
		"@id": `${WEB_URL}/#location-ostrava`,
		name: appName,
		url: WEB_URL,
		telephone: "+420736532744",
		email: "info@fathers.cz",
		address: {
			"@type": "PostalAddress",
			streetAddress: "Mitrovická 128/66",
			postalCode: "72400",
			addressLocality: "Ostrava",
			addressCountry: "CZ",
		},
		parentOrganization: organizationRef,
	} as const;

	const coffeeStoreUrl = $derived(`${WEB_URL}${getAboutUsPath(tenant)}${getCoffeeStoreAnchor(tenant.locale)}`);

	const coffeeStoreSchema = $derived({
		"@type": "CafeOrCoffeeShop",
		"@id": `${WEB_URL}/#location-praha`,
		name: "Father's Coffee Store",
		url: coffeeStoreUrl,
		address: {
			"@type": "PostalAddress",
			streetAddress: "Bubenečská 43",
			addressLocality: "Praha 6",
			addressCountry: "CZ",
		},
		openingHoursSpecification: [
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
				opens: "10:00",
				closes: "19:00",
			},
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: "Saturday",
				opens: "08:00",
				closes: "14:00",
			},
		],
		parentOrganization: organizationRef,
	} as unknown as Extract<CafeOrCoffeeShop, object>);

	const socials = [
		{ link: "https://facebook.com/fatherscoffeeroastery/", label: "Facebook", icon: facebookIcon },
		{ link: "https://instagram.com/fathers_coffee_roastery/", label: "Instagram", icon: instagramIcon },
		{ link: "https://youtube.com/@fatherscoffee", label: "YouTube", icon: youtubeIcon },
	];

	const contacts = {
		b2c: {
			email: "info@fathers.cz",
			tel: "+420736532744",
			info: translate("b2cInfo"),
		},
		b2b: {
			email: "velkoobchod@fathers.cz",
			tel: "+420739892940",
			info: translate("b2bInfo"),
		},
	};

	const mapLink = "https://maps.app.goo.gl/ipBns6AQqruvtXyr8";
	const mapAlt = "Mitrovická 128/66, 724 00 Ostrava 24, Czechia";
</script>

<StructuredData data={roasterySchema} />
<StructuredData data={coffeeStoreSchema} />

<Layout>
	<Heading level={1} text={translate("heading")} class="font-antipol font-medium text-10" />
	<div class="grid lg:grid-cols-2 gap-29 mb-30">
		<div>
			{#each Object.values(contacts) as contact (contact)}
				<div class="flex flex-col text-fcrgray mt-14 first:mt-4">
					<p class="mb-6">
						<Formatted text={contact.info} />
					</p>

					<a
						href="mailto:{contact.email}"
						target="_blank"
						class="block font-antipol text-5.5 underline text-fcrdarkblue hover:text-fcrbrown"
					>
						{contact.email}
					</a>

					<a
						href="tel:{contact.tel}"
						target="_blank"
						class="block font-antipol text-5.5 underline text-fcrdarkblue hover:text-fcrbrown"
					>
						{formatPhoneNumber(contact.tel)}
					</a>
				</div>
			{/each}

			<div class="block font-antipol text-5.5 text-fcrdarkgray mt-6">
				<div>
					<Formatted
						text={translate("cin", {
							cin: fathersCompany.cin,
						})}
					/>
				</div>
				<div>
					<Formatted
						text={translate("vat", {
							vat: fathersCompany.vat,
						})}
					/>
				</div>
			</div>

			<div class="mt-15 flex items-center gap-2">
				{#each socials as social (social.link)}
					<a
						href={social.link}
						target="_blank"
						referrerpolicy="no-referrer"
						rel="noreferrer"
						class={[
							"size-16 text-fcrdarkgray border-fcrlightgray border transition-colors hover:border-fcrgray",
							"flex justify-center items-center rounded-full border",
						]}
						title={social.label}
					>
						<Icon icon={social.icon} class="size-6 text-fcrdarkgray" />
					</a>
				{/each}
			</div>

			<a href={mapLink} target="_blank" class="block mt-13.5" title={mapAlt}>
				<Media
					class="w-174 h-128 max-w-full object-cover rounded-2xl"
					sourcesets={{
						">sm": mapImageSm,
						">md": mapImageMd,
						">lg": mapImageLg,
					}}
					source={{ type: "image/webp", src: mapImageXs }}
					alt={mapAlt}
					width={696}
					height={512}
				/>
			</a>
		</div>
		<ContactForm />
	</div>
</Layout>
